import React, { useCallback, useState } from 'react';

import { AuthenticatedRoute } from '@/app/constant';
import { TabOption, Tabs } from '@/components/chariot-ui';
import { useUpdateConfiguration } from '@/hooks/useConfiguration';
import { DogPileCardList } from '@/sections/customerManagement/components/dogPile/DogPileCardList';
import { DogPileSummaryHeader } from '@/sections/customerManagement/components/dogPile/DogPileSummaryHeader';
import { useDogPilePrioritization } from '@/sections/customerManagement/hooks/useDogPilePrioritization';
import { DogPileCategory } from '@/sections/customerManagement/types/dogPile.types';
import { useAuth } from '@/state/auth';

const SUB_TABS: TabOption[] = [
  { label: 'Subscriptions', value: 'subscriptions' },
  { label: 'Projects', value: 'projects' },
  { label: 'Freemium', value: 'freemium' },
];

export const DogPileDashboard: React.FC = () => {
  const [selectedSubTab, setSelectedSubTab] =
    useState<DogPileCategory>('subscriptions');

  const {
    subscriptions,
    projects,
    freemium,
    subscriptionTierCounts,
    projectTierCounts,
    freemiumTierCounts,
    isLoading,
  } = useDogPilePrioritization();

  const { startImpersonation, isPraetorianUser } = useAuth();

  // Per-email dogpile config update — hook is invoked at dashboard level,
  // target email is sent via the configuration PUT body.
  // We use a shared hook that accepts an email so each card gets its own context.
  const handleViewAccount = useCallback(
    (email: string) => {
      if (!isPraetorianUser) return;
      startImpersonation(email, AuthenticatedRoute.INSIGHTS);
    },
    [startImpersonation, isPraetorianUser]
  );

  const selectedTab =
    SUB_TABS.find(t => t.value === selectedSubTab) ?? SUB_TABS[0];

  const customers =
    selectedSubTab === 'subscriptions'
      ? subscriptions
      : selectedSubTab === 'projects'
        ? projects
        : freemium;
  const tierCounts =
    selectedSubTab === 'subscriptions'
      ? subscriptionTierCounts
      : selectedSubTab === 'projects'
        ? projectTierCounts
        : freemiumTierCounts;
  const categoryLabel =
    selectedSubTab === 'subscriptions'
      ? 'Subscriptions'
      : selectedSubTab === 'projects'
        ? 'Projects'
        : 'Freemium';

  return (
    <div className="flex flex-col gap-4">
      {/* Secondary tabs */}
      <Tabs
        tabs={SUB_TABS}
        selected={selectedTab}
        onSelect={tab => setSelectedSubTab(tab.value as DogPileCategory)}
        variant="secondary"
        align="left"
      />

      {/* Summary header */}
      <DogPileSummaryHeader
        tierCounts={tierCounts}
        categoryLabel={categoryLabel}
      />

      {/* Card list */}
      <DogPileCardListWrapper
        customers={customers}
        isLoading={isLoading}
        onViewAccount={handleViewAccount}
      />
    </div>
  );
};

/**
 * Wrapper that instantiates per-customer dogpile update hooks via a child
 * component pattern. Since hooks cannot be called conditionally, we provide
 * a StartDogpileHandler component that owns the mutation for a given email.
 */
interface DogPileCardListWrapperProps {
  customers: ReturnType<typeof useDogPilePrioritization>['subscriptions'];
  isLoading: boolean;
  onViewAccount: (email: string) => void;
}

function DogPileCardListWrapper({
  customers,
  isLoading,
  onViewAccount,
}: DogPileCardListWrapperProps) {
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [dogpileEndDate, setDogpileEndDate] = useState('');

  const handleStartDogpile = useCallback((email: string) => {
    setPendingEmail(email);
  }, []);

  const handleCancelModal = useCallback(() => {
    setPendingEmail(null);
    setDogpileEndDate('');
  }, []);

  return (
    <>
      <DogPileCardList
        customers={customers}
        isLoading={isLoading}
        onStartDogpile={handleStartDogpile}
        onViewAccount={onViewAccount}
      />

      {/* Dogpile start modal */}
      {pendingEmail && (
        <StartDogpileModal
          email={pendingEmail}
          endDate={dogpileEndDate}
          onEndDateChange={setDogpileEndDate}
          onConfirm={() => {
            setPendingEmail(null);
            setDogpileEndDate('');
          }}
          onCancel={handleCancelModal}
        />
      )}
    </>
  );
}

interface StartDogpileModalProps {
  email: string;
  endDate: string;
  onEndDateChange: (date: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

function StartDogpileModal({
  email,
  endDate,
  onEndDateChange,
  onConfirm,
  onCancel,
}: StartDogpileModalProps) {
  const updateConfig = useUpdateConfiguration({ email });
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleSubmit = useCallback(() => {
    if (!endDate) {
      setValidationError('Please select an end date');
      return;
    }
    const endsAt = new Date(endDate + 'T23:59:00Z');
    if (isNaN(endsAt.getTime())) {
      setValidationError('Invalid date');
      return;
    }
    const now = new Date();
    if (endsAt <= now) {
      setValidationError('End date must be in the future');
      return;
    }
    const twoYearsFromNow = new Date(now);
    twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2);
    if (endsAt > twoYearsFromNow) {
      setValidationError('End date must be within 2 years');
      return;
    }
    setValidationError(null);
    updateConfig.mutate(
      {
        name: 'evm#dogpile',
        value: {
          startedAt: new Date().toISOString(),
          endsAt: endsAt.toISOString(),
        },
      },
      {
        onSuccess: () => {
          onConfirm();
        },
      }
    );
  }, [endDate, updateConfig, onConfirm]);

  const today = new Date().toISOString().split('T')[0];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={e => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="bg-chariot-background-secondary border-chariot-stroke w-full max-w-sm rounded-lg border p-6 shadow-xl">
        <h3 className="text-chariot-text-primary mb-1 text-base font-semibold">
          Start Dogpile
        </h3>
        <p className="text-chariot-text-secondary mb-4 text-xs">{email}</p>

        <label
          htmlFor="dogpile-end-date"
          className="text-chariot-text-secondary mb-1 block text-xs font-medium"
        >
          Dogpile end date
        </label>
        <input
          id="dogpile-end-date"
          type="date"
          min={today}
          value={endDate}
          onChange={e => onEndDateChange(e.target.value)}
          aria-invalid={!!validationError}
          aria-describedby="dogpile-validation-error"
          className="border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary mb-4 w-full rounded border px-3 py-2 text-sm focus:outline-hidden"
        />

        {validationError && (
          <p
            id="dogpile-validation-error"
            role="alert"
            className="text-chariot-stroke-critical mb-3 text-xs"
          >
            {validationError}
          </p>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="border-chariot-stroke text-chariot-text-primary hover:bg-chariot-background-primary rounded border px-4 py-1.5 text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!endDate || updateConfig.isPending}
            className="bg-chariot-brand-primary hover:bg-chariot-brand-primary/90 rounded px-4 py-1.5 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            {updateConfig.isPending ? 'Starting...' : 'Start'}
          </button>
        </div>
      </div>
    </div>
  );
}
